/**
 * Utilidades de formateo
 * Funciones para formatear monedas, fechas, números, etc.
 */
import dayjs from '../config/dayjs';

/**
 * Formatear moneda
 * @param {number} amount - Monto a formatear
 * @param {string} currency - Código de moneda (USD, VES, etc.)
 * @returns {string} Monto formateado
 */
export const formatCurrency = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '-';
  }

  const currencySymbols = {
    USD: '$',
    VES: 'Bs.',
    EUR: '€',
    ARS: '$',
  };

  const symbol = currencySymbols[currency] || currency;
  const formatted = new Intl.NumberFormat('es-VE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

  return `${symbol} ${formatted}`;
};

/**
 * Formatear fecha
 * @param {Date|string} date - Fecha a formatear
 * @param {string} formatStr - Formato de salida (default: DD/MM/YYYY)
 * @returns {string} Fecha formateada
 */
export const formatDate = (date, formatStr = 'DD/MM/YYYY') => {
  if (!date) return '-';

  try {
    return dayjs(date).locale('es').format(formatStr);
  } catch (error) {
    console.error('Error al formatear fecha:', error);
    return '-';
  }
};

/**
 * Formatear fecha y hora
 * @param {Date|string} date - Fecha a formatear
 * @returns {string} Fecha y hora formateada
 */
export const formatDateTime = (date) => {
  if (!date) return '-';

  try {
    return dayjs(date).locale('es').format('DD/MM/YYYY HH:mm:ss');
  } catch (error) {
    console.error('Error al formatear fecha y hora:', error);
    return '-';
  }
};

/**
 * Formatear fecha de forma relativa (hace X tiempo)
 * @param {Date|string} date - Fecha a formatear
 * @returns {string} Fecha relativa
 */
export const formatRelativeDate = (date) => {
  if (!date) return '-';

  try {
    const dateObj = dayjs(date);
    const now = dayjs();
    const diffMins = now.diff(dateObj, 'minute');
    const diffHours = now.diff(dateObj, 'hour');
    const diffDays = now.diff(dateObj, 'day');

    if (diffMins < 1) return 'Hace un momento';
    if (diffMins < 60) return `Hace ${diffMins} minuto${diffMins > 1 ? 's' : ''}`;
    if (diffHours < 24) return `Hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    if (diffDays < 7) return `Hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;

    return formatDate(date);
  } catch (error) {
    console.error('Error al formatear fecha relativa:', error);
    return '-';
  }
};

/**
 * Formatear número
 * @param {number} number - Número a formatear
 * @param {number} decimals - Cantidad de decimales
 * @returns {string} Número formateado
 */
export const formatNumber = (number, decimals = 2) => {
  if (number === null || number === undefined || isNaN(number)) {
    return '-';
  }

  return new Intl.NumberFormat('es-VE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number);
};

/**
 * Formatear porcentaje
 * @param {number} value - Valor a formatear (0-100)
 * @param {number} decimals - Cantidad de decimales
 * @returns {string} Porcentaje formateado
 */
export const formatPercentage = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }

  return `${formatNumber(value, decimals)}%`;
};

/**
 * Formatear número de teléfono
 * @param {string} phone - Número de teléfono
 * @returns {string} Teléfono formateado
 */
export const formatPhone = (phone) => {
  if (!phone) return '-';

  // Formato para Venezuela: 0414-1234567
  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.length === 11) {
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
  }

  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  }

  return phone;
};

/**
 * Formatear RUC/CUIT
 * @param {string} ruc - RUC o CUIT
 * @returns {string} RUC formateado
 */
export const formatRUC = (ruc) => {
  if (!ruc) return '-';

  // Formato para Venezuela: J-12345678-9
  const cleaned = ruc.replace(/[^A-Z0-9]/gi, '');

  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 1)}-${cleaned.slice(1, 9)}-${cleaned.slice(9)}`;
  }

  return ruc;
};

/**
 * Formatear tamaño de archivo
 * @param {number} bytes - Tamaño en bytes
 * @returns {string} Tamaño formateado
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  if (!bytes) return '-';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Acortar texto
 * @param {string} text - Texto a acortar
 * @param {number} maxLength - Longitud máxima
 * @returns {string} Texto acortado
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Capitalizar primera letra
 * @param {string} text - Texto a capitalizar
 * @returns {string} Texto capitalizado
 */
export const capitalize = (text) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Capitalizar cada palabra
 * @param {string} text - Texto a capitalizar
 * @returns {string} Texto capitalizado
 */
export const capitalizeWords = (text) => {
  if (!text) return '';
  return text
    .split(' ')
    .map(word => capitalize(word))
    .join(' ');
};

export default {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatRelativeDate,
  formatNumber,
  formatPercentage,
  formatPhone,
  formatRUC,
  formatFileSize,
  truncateText,
  capitalize,
  capitalizeWords,
};
